import React from 'react'
import { Box, Button as ChakraButton, Flex, Text, useDisclosure } from '@chakra-ui/core'
import { useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Menu } from 'lucide-react'
import LocaleText from 'components/LocaleText'
import MobileMenu from 'components/Header/MobileMenu'
import { Button } from 'components/ui'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { DEVICE_TYPES } from 'constants/UI'
import { usePageConfig } from 'contexts'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'

export const PAGE_TITLE_HEIGHT = 54

type TitleEntry = { type: 'locale'; key: string } | { type: 'literal'; text: string } | { type: 'none' }

const getTitle = (pathname: string): TitleEntry => {
  if (pathname.startsWith('/dashboard/analytics')) return { type: 'locale', key: 'data_analytics_title' }
  if (pathname.startsWith('/dashboard/tasks-contexts-list') || pathname.startsWith('dashboard/tasks-contexts-list'))
    return { type: 'locale', key: 'task_overview_title' }
  if (pathname.startsWith('/dashboard-new')) return { type: 'locale', key: 'tasks_dashboard' }
  if (pathname.startsWith('/dashboard-service-calls')) return { type: 'literal', text: 'Service Calls Dashboard' }
  if (pathname.startsWith('/dashboard')) return { type: 'locale', key: 'tasks_dashboard' }
  if (pathname === '/' || pathname.startsWith('/search')) return { type: 'none' }
  if (pathname.startsWith('/hub')) return { type: 'locale', key: 'tasks_dashboard' }
  if (pathname.startsWith('/ranking')) return { type: 'literal', text: 'Ranking' }
  if (pathname.startsWith('/reports')) return { type: 'locale', key: 'reports_sidebar' }
  if (pathname.startsWith('/custom-reports')) return { type: 'literal', text: 'Custom Reports' }
  if (pathname.startsWith('/improving-sales/campaign')) return { type: 'locale', key: 'data_tasks' }
  if (pathname.startsWith('/improving-sales')) return { type: 'locale', key: 'data_tasks' }
  if (pathname.startsWith('/analytics')) return { type: 'locale', key: 'data_analytics_title' }
  if (pathname.startsWith('/product-performance')) return { type: 'locale', key: 'product_performance' }
  if (pathname.startsWith('/chat')) return { type: 'literal', text: 'Chat' }
  if (pathname.startsWith('/organization-tree')) return { type: 'locale', key: 'organization_tree' }
  if (pathname.startsWith('/sales-opportunities')) return { type: 'locale', key: 'sales_opportunities' }
  if (pathname.startsWith('/create-task-v2')) return { type: 'none' }
  if (pathname.startsWith('/integrations')) return { type: 'literal', text: 'Integrations' }
  if (pathname.startsWith('/admin')) return { type: 'literal', text: 'Admin Console' }
  if (pathname.startsWith('/setup')) return { type: 'literal', text: 'Setup' }
  if (pathname.startsWith('/external-app')) return { type: 'none' }
  return { type: 'none' }
}

const PageTitle: React.FC = () => {
  const { pathname } = useLocation()
  const { windowWidth } = useWindowDimensions()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { config } = usePageConfig()
  const isRtl = useSelector(isRtlSelector)
  const title = getTitle(pathname)
  const isDesktop = DEVICE_TYPES.DESKTOP(windowWidth)

  if (isDesktop) {
    if (title.type === 'none') return null
    return (
      <Flex
        px="16px"
        bg="transparent"
        align="center"
        style={{ height: `${PAGE_TITLE_HEIGHT}px`, flexShrink: 0, gap: '8px' }}
      >
        {config.onBack && (
          <Button variant="icon" h="30px" w="30px" minW="30px" onClick={config.onBack} aria-label="go back">
            {isRtl ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </Button>
        )}
        {config.icon && (
          <Box display="flex" alignItems="center" flexShrink={0} color="#64748B">
            {config.icon}
          </Box>
        )}
        <Text fontSize="18px" fontWeight="700" color="#1a202c" lineHeight="1">
          {title.type === 'locale' ? <LocaleText text={title.key} /> : title.text}
        </Text>
        {config.titleActions && <Box>{config.titleActions()}</Box>}
        {config.actions && <Box ml="auto">{config.actions()}</Box>}
      </Flex>
    )
  }

  // Mobile / laptop: always show burger button + optional title
  return (
    <>
      <Flex
        px="8px"
        bg="transparent"
        alignItems="center"
        borderBottom="1px solid"
        borderColor="gray.100"
        style={{ height: `${PAGE_TITLE_HEIGHT}px`, flexShrink: 0, gap: '4px' }}
      >
        {config.onBack ? (
          <Button variant="icon" h="32px" w="32px" minW="32px" onClick={config.onBack} aria-label="go back">
            {isRtl ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </Button>
        ) : (
          <ChakraButton variant="ghost" onClick={onOpen} p="8px" mr="4px" minWidth="auto">
            <Menu size={22} color="#1a202c" />
          </ChakraButton>
        )}
        {config.icon && (
          <Box display="flex" alignItems="center" flexShrink={0} color="#64748B" mr="4px">
            {config.icon}
          </Box>
        )}
        {title.type === 'locale' && (
          <Text fontSize="18px" fontWeight="700" color="#1a202c" lineHeight="1">
            <LocaleText text={title.key} />
          </Text>
        )}
        {title.type === 'literal' && (
          <Text fontSize="18px" fontWeight="700" color="#1a202c" lineHeight="1">
            {title.text}
          </Text>
        )}
        {config.titleActions && <Box>{config.titleActions()}</Box>}
        {config.actions && <Box ml="auto">{config.actions()}</Box>}
      </Flex>
      <MobileMenu isOpen={isOpen} onClose={onClose} />
    </>
  )
}

export default PageTitle
